import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import mermaid from 'mermaid';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface GroundedQuote {
  text: string;
  isVerified: boolean;
  confidence: number;
  sourceParticipant?: string;
}

interface Theme {
  name: string;
  description: string;
  sentiment: 'positive' | 'neutral' | 'negative' | 'mixed';
  frequency: number;
  quotes: string[];
  groundedQuotes?: GroundedQuote[];
}

interface Insight {
  title: string;
  description: string;
  category: 'strength' | 'weakness' | 'opportunity' | 'risk';
  impact: 'high' | 'medium' | 'low';
}

interface Improvement {
  area: string;
  suggestion: string;
  priority: 'high' | 'medium' | 'low';
  effort: 'low' | 'medium' | 'high';
}

interface QuestionAnalysis {
  question: string;
  responseCount: number;
  commonAnswers: string[];
  sentiment: 'positive' | 'neutral' | 'negative' | 'mixed';
}

interface Analytics {
  summary: string;
  themes: Theme[];
  insights: Insight[];
  improvements: Improvement[];
  sentimentBreakdown: {
    overall: 'positive' | 'neutral' | 'negative' | 'mixed';
    score: number;
    breakdown: {
      positive: number;
      neutral: number;
      negative: number;
    };
  };
  keyMetrics: Array<{
    label: string;
    value: string;
    trend: 'up' | 'down' | 'stable';
  }>;
  mermaidDiagram: string;
  questionAnalysis: QuestionAnalysis[];
  groundingStats?: {
    totalQuotes: number;
    verifiedQuotes: number;
    averageConfidence: number;
  };
}

interface AIAnalyticsProps {
  interviewId: string;
  responseCount: number;
  autoLoad?: boolean;
}

let mermaidInitialised = false;

function initialiseMermaid() {
  if (mermaidInitialised) return;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'base',
    securityLevel: 'loose',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    logLevel: 'error',
    themeVariables: {
      primaryColor: '#0066FF',
      primaryTextColor: '#fff',
      primaryBorderColor: '#0055DD',
      lineColor: '#666',
      secondaryColor: '#1a1a2e',
      tertiaryColor: '#16213e',
      background: '#1F2937',
      mainBkg: '#1F2937',
      nodeBorder: '#4B5563',
      clusterBkg: '#374151',
      titleColor: '#F9FAFB',
      edgeLabelBackground: '#374151',
      fontSize: '14px',
    },
    flowchart: {
      htmlLabels: false,
      useMaxWidth: true,
      curve: 'basis',
      padding: 20,
      nodeSpacing: 50,
      rankSpacing: 50,
    },
  });
  mermaidInitialised = true;
}

/**
 * Sanitises AI-generated Mermaid syntax to prevent render failures.
 * Fixes common issues: special chars in labels, missing direction,
 * unbalanced brackets, semicolons, and HTML entities.
 */
function sanitizeMermaidSyntax(raw: string): string {
  if (!raw || typeof raw !== 'string') return 'flowchart TD\n    A["No data"]';

  let diagram = raw.trim();

  // Strip markdown code fences the AI sometimes wraps the diagram in
  diagram = diagram.replace(/^```(?:mermaid)?\s*/i, '').replace(/\s*```$/i, '');

  // Ensure it starts with a valid direction declaration
  if (!/^(flowchart|graph)\s+(TD|TB|LR|RL|BT)/i.test(diagram)) {
    if (/^(flowchart|graph)\s*$/im.test(diagram)) {
      diagram = diagram.replace(/^(flowchart|graph)\s*$/im, '$1 TD');
    } else {
      diagram = 'flowchart TD\n' + diagram;
    }
  }

  const lines = diagram.split('\n');
  const sanitised: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Skip empty lines and comments
    if (!line.trim() || line.trim().startsWith('%%')) {
      sanitised.push(line);
      continue;
    }

    // Keep the direction declaration as-is
    if (i === 0 && /^(flowchart|graph)\s+(TD|TB|LR|RL|BT)/i.test(line.trim())) {
      sanitised.push(line);
      continue;
    }

    // Keep classDef / style / linkStyle lines as-is
    if (/^\s*(classDef|style|linkStyle)\s+/i.test(line)) {
      sanitised.push(line);
      continue;
    }

    // Fix labels: replace problematic characters inside bracket-delimited labels
    line = line.replace(/\[([^\]]*)\]/g, (_match, label: string) => {
      let safe = label
        .replace(/"/g, "'")
        .replace(/[<>]/g, '')
        .replace(/[#](?!\d+;)/g, '')
        .replace(/&(?!amp;|lt;|gt;|quot;)/g, 'and')
        .replace(/;(?!$)/g, ',');
      return `["${safe.replace(/^"|"$/g, '')}"]`;
    });

    // Fix labels inside braces (decision nodes)
    line = line.replace(/\{([^}]*)\}/g, (_match, label: string) => {
      if (/^\s*(classDef|style)/i.test(line)) return `{${label}}`;
      let safe = label
        .replace(/"/g, "'")
        .replace(/[<>]/g, '')
        .replace(/[#](?!\d+;)/g, '')
        .replace(/&(?!amp;|lt;|gt;|quot;)/g, 'and')
        .replace(/;(?!$)/g, ',');
      return `{"${safe.replace(/^"|"$/g, '')}"}`;
    });

    // Fix labels inside round-bracket nodes ((...))
    line = line.replace(/\(\(([^)]*)\)\)/g, (_match, label: string) => {
      let safe = label.replace(/"/g, "'").replace(/[<>#]/g, '');
      return `(("${safe.replace(/^"|"$/g, '')}"))`;
    });

    // Remove trailing semicolons that break Mermaid parsing
    line = line.replace(/;\s*$/, '');

    // Fix edge labels: |label| should be |"label"|
    line = line.replace(/\|([^"|][^|]*[^"|])\|/g, (_match, label: string) => {
      return `|"${label.replace(/"/g, "'")}"|`;
    });

    sanitised.push(line);
  }

  return sanitised.join('\n');
}

/**
 * Extracts node labels from broken Mermaid syntax and builds a
 * minimal valid linear flowchart as a last-resort fallback.
 */
function buildFallbackDiagram(raw: string): string {
  const labels: string[] = [];

  // Extract text from bracket-delimited labels: [Label], ["Label"], {Label}, ((Label))
  const labelPattern = /\[["']?([^\]"']+)["']?\]|\{["']?([^}"']+)["']?\}|\(\(["']?([^)"']+)["']?\)\)/g;
  let match: RegExpExecArray | null;
  while ((match = labelPattern.exec(raw)) !== null) {
    const text = (match[1] || match[2] || match[3] || '').trim();
    if (text && text.length > 1 && !labels.includes(text)) {
      labels.push(text.length > 30 ? text.substring(0, 27) + '...' : text);
    }
  }

  if (labels.length === 0) {
    return 'flowchart TD\n    A["Process flow unavailable"]';
  }

  const lines: string[] = ['flowchart TD'];
  labels.forEach((label, i) => {
    const safe = label.replace(/"/g, "'").replace(/[<>#&;]/g, '');
    lines.push(`    N${i}["${safe}"]`);
    if (i > 0) {
      lines.push(`    N${i - 1} --> N${i}`);
    }
  });

  return lines.join('\n');
}

export function AIAnalytics({ interviewId, responseCount, autoLoad = false }: AIAnalyticsProps) {
  const { authFetch } = useAuthFetch();
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'overview' | 'themes' | 'insights' | 'improvements' | 'questions'>('overview');
  const [hasFetched, setHasFetched] = useState(false);
  const [diagramError, setDiagramError] = useState<string | null>(null);
  const diagramRef = useRef<HTMLDivElement>(null);
  const renderIdRef = useRef(0);

  const fetchAnalytics = useCallback(async () => {
    if (responseCount === 0) return;
    
    setIsLoading(true);
    setError(null);
    setDiagramError(null);
    setHasFetched(true);

    try {
      const res = await authFetch(`${API_URL}/interviews/${interviewId}/analyze`, {
        method: 'POST',
      });

      if (res.ok) {
        const data = await res.json();
        setAnalytics(data.analytics);
      } else {
        const errorData = await res.json();
        setError(errorData.error || 'Failed to analyze responses');
      }
    } catch {
      setError('Failed to analyze interview responses');
    } finally {
      setIsLoading(false);
    }
  }, [interviewId, responseCount, authFetch]);

  useEffect(() => {
    if (autoLoad && responseCount > 0 && !hasFetched && !analytics && !isLoading) {
      fetchAnalytics();
    }
  }, [autoLoad, responseCount, hasFetched, analytics, isLoading, fetchAnalytics]);

  useEffect(() => {
    if (!analytics?.mermaidDiagram || activeSection !== 'overview') return;

    const renderDiagram = async () => {
      const el = diagramRef.current;
      if (!el) return;

      renderIdRef.current += 1;
      const thisRender = renderIdRef.current;

      try {
        initialiseMermaid();
        el.innerHTML = '';
        setDiagramError(null);

        const sanitised = sanitizeMermaidSyntax(analytics.mermaidDiagram);
        const id = `mermaid-analytics-${Date.now()}-${Math.random().toString(36).substring(7)}`;
        const { svg } = await mermaid.render(id, sanitised);

        if (thisRender === renderIdRef.current && el) {
          el.innerHTML = svg;
          const svgEl = el.querySelector('svg');
          if (svgEl) {
            svgEl.removeAttribute('height');
            svgEl.style.maxWidth = '100%';
            svgEl.style.height = 'auto';
          }
        }
      } catch (firstErr) {
        console.warn('Mermaid render failed, attempting minimal fallback:', firstErr);

        // Self-heal: build a minimal valid diagram from the raw text
        try {
          const fallback = buildFallbackDiagram(analytics.mermaidDiagram);
          const fallbackId = `mermaid-fallback-${Date.now()}`;
          const { svg } = await mermaid.render(fallbackId, fallback);

          if (thisRender === renderIdRef.current && el) {
            el.innerHTML = svg;
            const svgEl = el.querySelector('svg');
            if (svgEl) {
              svgEl.removeAttribute('height');
              svgEl.style.maxWidth = '100%';
              svgEl.style.height = 'auto';
            }
          }
        } catch (secondErr) {
          console.error('Mermaid fallback also failed:', secondErr);
          if (thisRender === renderIdRef.current) {
            setDiagramError('The process flow diagram could not be rendered. Try regenerating the analysis.');
          }
        }
      }
    };

    // Small delay so the DOM element is visible after tab switch
    const timer = setTimeout(renderDiagram, 50);
    return () => clearTimeout(timer);
  }, [analytics?.mermaidDiagram, activeSection]);

  const handleRetryDiagram = useCallback(() => {
    setDiagramError(null);
    renderIdRef.current += 1;
    // Force re-render by briefly switching away and back
    setActiveSection('themes');
    setTimeout(() => setActiveSection('overview'), 0);
  }, []);

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return 'text-emerald-500';
      case 'negative': return 'text-red-500';
      case 'mixed': return 'text-amber-500';
      default: return 'text-gray-500';
    }
  };

  const getSentimentBg = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return 'bg-emerald-500/10 border-emerald-500/20';
      case 'negative': return 'bg-red-500/10 border-red-500/20';
      case 'mixed': return 'bg-amber-500/10 border-amber-500/20';
      default: return 'bg-gray-500/10 border-gray-500/20';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'strength':
        return <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
      case 'weakness':
        return <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>;
      case 'opportunity':
        return <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>;
      case 'risk':
        return <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
      default:
        return null;
    }
  };

  const getPriorityBadge = (priority: string) => {
    const colors = {
      high: 'bg-red-500/20 text-red-400 border-red-500/30',
      medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      low: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    };
    return colors[priority as keyof typeof colors] || colors.medium;
  };

  if (responseCount === 0) {
    return (
      <div className="card text-center py-12">
        <svg className="w-16 h-16 mx-auto text-muted mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h3 className="text-lg font-semibold text-foreground mb-2">No Responses Yet</h3>
        <p className="text-muted">AI analytics will be available once you have submitted responses.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="card text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Analyzing Responses...</h3>
        <p className="text-muted">AI is extracting themes, insights, and improvements from your interview data.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <svg className="w-16 h-16 mx-auto text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3 className="text-lg font-semibold text-foreground mb-2">Analysis Failed</h3>
        <p className="text-muted mb-4">{error}</p>
        <button onClick={fetchAnalytics} className="btn btn-primary">Try Again</button>
      </div>
    );
  }

  if (!analytics) return null;

  const totalSentiment = analytics.sentimentBreakdown.breakdown.positive + analytics.sentimentBreakdown.breakdown.neutral + analytics.sentimentBreakdown.breakdown.negative;

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="card bg-gradient-to-br from-primary/10 to-transparent border-primary/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">Executive Summary</h3>
            <p className="text-muted leading-relaxed">{analytics.summary}</p>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {analytics.keyMetrics.slice(0, 4).map((metric, i) => (
          <div key={i} className="card text-center">
            <div className="text-2xl font-bold text-foreground">{metric.value}</div>
            <div className="text-xs text-muted mt-1">{metric.label}</div>
            <div className={`text-xs mt-1 ${metric.trend === 'up' ? 'text-emerald-500' : metric.trend === 'down' ? 'text-red-500' : 'text-gray-500'}`}>
              {metric.trend === 'up' ? '↑' : metric.trend === 'down' ? '↓' : '→'}
            </div>
          </div>
        ))}
      </div>

      {/* Sentiment Overview */}
      <div className="card">
        <h3 className="text-lg font-semibold text-foreground mb-4">Sentiment Analysis</h3>
        <div className="flex items-center gap-4 mb-4">
          <div className={`text-xl font-bold capitalize ${getSentimentColor(analytics.sentimentBreakdown.overall)}`}>
            {analytics.sentimentBreakdown.overall}
          </div>
          <div className="text-muted">Overall Sentiment</div>
        </div>
        <div className="h-4 bg-user-msg rounded-full overflow-hidden flex">
          <div className="bg-emerald-500 transition-all" style={{ width: `${(analytics.sentimentBreakdown.breakdown.positive / totalSentiment) * 100}%` }} />
          <div className="bg-gray-400 transition-all" style={{ width: `${(analytics.sentimentBreakdown.breakdown.neutral / totalSentiment) * 100}%` }} />
          <div className="bg-red-500 transition-all" style={{ width: `${(analytics.sentimentBreakdown.breakdown.negative / totalSentiment) * 100}%` }} />
        </div>
        <div className="flex justify-between text-xs text-muted mt-2">
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-emerald-500" /> Positive ({analytics.sentimentBreakdown.breakdown.positive})</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-gray-400" /> Neutral ({analytics.sentimentBreakdown.breakdown.neutral})</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500" /> Negative ({analytics.sentimentBreakdown.breakdown.negative})</span>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'overview' as const, label: 'Process Flow' },
          { id: 'themes' as const, label: `Themes (${analytics.themes.length})` },
          { id: 'insights' as const, label: `Insights (${analytics.insights.length})` },
          { id: 'improvements' as const, label: `Improvements (${analytics.improvements.length})` },
          { id: 'questions' as const, label: 'By Question' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeSection === tab.id
                ? 'bg-primary text-white'
                : 'bg-user-msg text-muted hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Process Flow / Mermaid Diagram */}
      {activeSection === 'overview' && (
        <div className="card">
          <h3 className="text-lg font-semibold text-foreground mb-4">Discovered Process Flow</h3>
          {diagramError ? (
            <div className="bg-user-msg rounded-lg p-6 min-h-[200px] flex items-center justify-center">
              <div className="text-center max-w-md">
                <svg className="w-10 h-10 mx-auto text-amber-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm text-muted mb-3">{diagramError}</p>
                <button onClick={handleRetryDiagram} className="btn btn-primary text-sm">
                  Retry Diagram
                </button>
              </div>
            </div>
          ) : (
            <div
              ref={diagramRef}
              className="overflow-x-auto bg-user-msg rounded-lg p-3 sm:p-4 min-h-[200px] flex items-center justify-center [&_svg]:max-w-full [&_svg]:h-auto"
            />
          )}
        </div>
      )}

      {/* Themes */}
      {activeSection === 'themes' && (
        <div className="space-y-4">
          {/* Grounding stats banner if available */}
          {analytics.groundingStats && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-blue-600 dark:text-blue-400">
                  Quote Verification: {analytics.groundingStats.verifiedQuotes}/{analytics.groundingStats.totalQuotes} quotes verified
                </span>
              </div>
              <span className="text-xs text-muted">
                {Math.round(analytics.groundingStats.averageConfidence * 100)}% avg confidence
              </span>
            </div>
          )}
          
          {analytics.themes.map((theme, i) => (
            <div key={i} className={`card border ${getSentimentBg(theme.sentiment)}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-foreground">{theme.name}</h4>
                  <p className="text-sm text-muted mt-1">{theme.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium capitalize ${getSentimentColor(theme.sentiment)}`}>{theme.sentiment}</span>
                  <span className="text-xs bg-card px-2 py-1 rounded">{theme.frequency}x</span>
                </div>
              </div>
              {/* Display grounded quotes with verification status if available */}
              {theme.groundedQuotes && theme.groundedQuotes.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-xs text-muted font-medium flex items-center gap-2">
                    Key Quotes:
                    <span className="text-xs text-emerald-500">
                      ({theme.groundedQuotes.filter(q => q.isVerified).length}/{theme.groundedQuotes.length} verified)
                    </span>
                  </div>
                  {theme.groundedQuotes.slice(0, 3).map((gq, qi) => (
                    <div key={qi} className={`text-sm italic pl-3 border-l-2 ${
                      gq.isVerified 
                        ? 'text-foreground border-emerald-500' 
                        : 'text-muted border-amber-500'
                    }`}>
                      <div className="flex items-start justify-between gap-2">
                        <span>"{gq.text}"</span>
                        <div className="flex items-center gap-1 shrink-0">
                          {gq.isVerified ? (
                            <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          )}
                          <span className={`text-xs ${gq.isVerified ? 'text-emerald-500' : 'text-amber-500'}`}>
                            {Math.round(gq.confidence * 100)}%
                          </span>
                        </div>
                      </div>
                      {gq.sourceParticipant && (
                        <div className="text-xs text-muted mt-0.5 not-italic">— {gq.sourceParticipant}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : theme.quotes.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs text-muted font-medium">Key Quotes:</div>
                  {theme.quotes.slice(0, 3).map((quote, qi) => (
                    <div key={qi} className="text-sm italic text-muted pl-3 border-l-2 border-border">
                      "{quote}"
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Insights */}
      {activeSection === 'insights' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {analytics.insights.map((insight, i) => (
            <div key={i} className="card">
              <div className="flex items-start gap-3">
                {getCategoryIcon(insight.category)}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-foreground">{insight.title}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getPriorityBadge(insight.impact)}`}>
                      {insight.impact} impact
                    </span>
                  </div>
                  <p className="text-sm text-muted">{insight.description}</p>
                  <div className="text-xs text-muted mt-2 capitalize">Category: {insight.category}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Improvements */}
      {activeSection === 'improvements' && (
        <div className="space-y-4">
          {analytics.improvements.map((improvement, i) => (
            <div key={i} className="card">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground">{improvement.area}</h4>
                  <p className="text-sm text-muted mt-1">{improvement.suggestion}</p>
                </div>
                <div className="flex flex-col items-end gap-1 ml-4">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${getPriorityBadge(improvement.priority)}`}>
                    {improvement.priority} priority
                  </span>
                  <span className="text-xs text-muted">{improvement.effort} effort</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Question Analysis */}
      {activeSection === 'questions' && (
        <div className="space-y-4">
          {analytics.questionAnalysis.map((qa, i) => (
            <div key={i} className="card">
              <div className="flex items-start justify-between mb-3">
                <h4 className="font-semibold text-foreground flex-1">{qa.question}</h4>
                <span className={`text-xs font-medium capitalize ${getSentimentColor(qa.sentiment)}`}>{qa.sentiment}</span>
              </div>
              <div className="text-xs text-muted mb-2">{qa.responseCount} responses</div>
              <div className="space-y-1">
                {qa.commonAnswers.slice(0, 3).map((answer, ai) => (
                  <div key={ai} className="text-sm text-muted flex items-start gap-2">
                    <span className="text-primary">•</span>
                    {answer}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-center gap-3 pt-4">
        <button onClick={fetchAnalytics} className="btn text-sm">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Regenerate Analysis
        </button>
        {analytics && (
          <>
            <button
              onClick={() => {
                const blob = new Blob([JSON.stringify(analytics, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `analytics-${interviewId}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              className="btn text-sm"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export JSON
            </button>
            <button
              onClick={async () => {
                const { default: jsPDF } = await import('jspdf');
                const doc = new jsPDF();
                let y = 20;

                doc.setFontSize(18);
                doc.text('Interview Analytics Report', 14, y);
                y += 12;

                doc.setFontSize(11);
                doc.text('Summary', 14, y);
                y += 7;
                doc.setFontSize(9);
                const summaryLines = doc.splitTextToSize(analytics.summary, 180);
                doc.text(summaryLines, 14, y);
                y += summaryLines.length * 5 + 8;

                if (analytics.themes.length > 0) {
                  doc.setFontSize(11);
                  doc.text('Key Themes', 14, y);
                  y += 7;
                  doc.setFontSize(9);
                  for (const theme of analytics.themes) {
                    if (y > 270) { doc.addPage(); y = 20; }
                    doc.text(`• ${theme.name}: ${theme.description}`, 14, y);
                    y += 6;
                  }
                  y += 4;
                }

                if (analytics.insights.length > 0) {
                  doc.setFontSize(11);
                  if (y > 260) { doc.addPage(); y = 20; }
                  doc.text('Insights', 14, y);
                  y += 7;
                  doc.setFontSize(9);
                  for (const insight of analytics.insights) {
                    if (y > 270) { doc.addPage(); y = 20; }
                    const insightLines = doc.splitTextToSize(
                      `[${insight.category.toUpperCase()}] ${insight.title}: ${insight.description}`,
                      180
                    );
                    doc.text(insightLines, 14, y);
                    y += insightLines.length * 5 + 3;
                  }
                  y += 4;
                }

                if (analytics.improvements.length > 0) {
                  doc.setFontSize(11);
                  if (y > 260) { doc.addPage(); y = 20; }
                  doc.text('Improvements', 14, y);
                  y += 7;
                  doc.setFontSize(9);
                  for (const imp of analytics.improvements) {
                    if (y > 270) { doc.addPage(); y = 20; }
                    const impLines = doc.splitTextToSize(
                      `[${imp.priority.toUpperCase()}] ${imp.area}: ${imp.suggestion}`,
                      180
                    );
                    doc.text(impLines, 14, y);
                    y += impLines.length * 5 + 3;
                  }
                }

                doc.save(`analytics-${interviewId}.pdf`);
              }}
              className="btn text-sm"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Export PDF
            </button>
          </>
        )}
      </div>
    </div>
  );
}
